import { getDateFromUnixSeconds } from 'modules/common/utils/getDateFromUnixSeconds';

import {
  BundleChargingModel,
  ChargingModel,
  PAYGChargingModel,
} from '../types';

type SplittedBundleModels = {
  active: BundleChargingModel[];
  inactive: BundleChargingModel[];
};

const splitModels = (models: BundleChargingModel[]) => {
  const now = new Date();

  return models.reduce<SplittedBundleModels>(
    (result, model) => {
      const isExpired = now > getDateFromUnixSeconds(model.expires);
      const isUsed = model.usage.percent >= 100;

      if (isExpired || isUsed) {
        result.inactive.push(model);
      } else {
        result.active.push(model);
      }

      return result;
    },
    {
      active: [],
      inactive: [],
    },
  );
};

const compareModelExpiration = (
  modelA: BundleChargingModel,
  modelB: BundleChargingModel,
) => modelA.expires - modelB.expires;

export interface SortChargingModelsParams {
  dealChargingModels: BundleChargingModel[] | undefined;
  freemiumChargingModel: BundleChargingModel | undefined;
  packageChargingModels: BundleChargingModel[] | undefined;
  paygChargingModel: PAYGChargingModel | undefined;
  promoChargingModel: BundleChargingModel | undefined;
}

export const sortChargingModels = ({
  dealChargingModels = [],
  freemiumChargingModel,
  packageChargingModels = [],
  paygChargingModel,
  promoChargingModel,
}: SortChargingModelsParams): ChargingModel[] => {
  const { active: activePackageModels, inactive: inactivePackageModels } =
    splitModels(packageChargingModels);

  const { active: activeDealModels, inactive: inactiveDealModels } =
    splitModels(dealChargingModels);

  const activeBundleModels = [
    ...activePackageModels.sort(compareModelExpiration),
    ...activeDealModels.sort(compareModelExpiration),
  ];

  const inactiveBundleModels = [
    ...inactivePackageModels.sort(compareModelExpiration),
    ...inactiveDealModels.sort(compareModelExpiration),
  ];

  // first show active deals or packages
  const chargingModels: ChargingModel[] = [...activeBundleModels];

  // if payg is present, show it after active deals or packages
  if (paygChargingModel) {
    chargingModels.push(paygChargingModel);
  }

  // finally show inactive deals or packages
  chargingModels.push(...inactiveBundleModels);

  // if freemium charging model is present, show it first
  if (freemiumChargingModel) {
    chargingModels.unshift(freemiumChargingModel);
  }

  // if promo charging model is present, show it first
  if (promoChargingModel) {
    chargingModels.unshift(promoChargingModel);
  }

  return chargingModels;
};
